import { useEffect, useState } from 'react';
import { useResize } from 'common/hooks/resize';
import { useCompareUrlWithProjectId, useNavigateWithProjectId } from 'common/hooks/route';
import useApiLoadingState from 'common/hooks/useApiLoadingState';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import { Feature } from 'common/models/page-config.models';
import {
  USER_ROUTES,
  REPORTS_ROUTES,
  CAPTURE_ROUTES,
  PROFILE_CAPTURE_ROUTES,
  ANALYZE_ROUTES,
  ALIGNMENT_ROUTES,
  INSIGHTS_ROUTES,
  COACH_ROUTES,
} from 'routes/routes.const';
import { KWELLO_ICON } from '../icon/KwelloSvgIcon';
import { useDispatch, useSelector } from 'react-redux';
import { hideConfirm, showConfirm } from 'common/store/slices/confirm.slice';
import { AppConfirm } from '../dialog/Confirm';
import { Typography } from '@progress/kendo-react-common';
import { TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { selectPage } from 'common/store/slices/page-composer.slice';
import { ReportModel } from 'common/models/report.model';
import { WidgetModel } from 'common/models/widget.model';
import { PageComposerModel } from 'common/models/page-composer.model';
import { useCreateReportMutation } from 'common/store/api/report.api';
import { RootState } from 'common/store';
import { selectCommonFilters } from 'common/store/slices/common-filters.slice';
import {
  selectEndDate,
  selectStartDate,
  selectVisualData,
  selectVisualType,
} from 'common/store/slices/date-filter.slice';
import { selectFilterParent } from 'common/store/slices/filter-parent.slice';
import { selectDatasetQuestion } from 'common/store/slices/dataset.slice';
import { CommonFilterList, CommonFilterObject } from 'common/models/filter.model';
import useDatasetQuestions from 'common/hooks/useDatasetQuestions';
import KwelloIconButton from '../KwelloButton/KwelloIconButton';
import { PLANNING_ROUTES } from 'routes/routes.planning.const';
import { useParams } from 'react-router-dom';
import { uniqueString } from '../widgets/widget-utils/widgetUtils';
import { selectWidgetOptions } from 'common/store/slices/widget-config.slice';
import { WidgetOptions } from 'common/contexts/useWidgetOptionsContext';
import usePersistWidgetOptions from 'common/hooks/usePersistWidgetOptions';
import {
  DEFAULT_END_DATE,
  DEFAULT_FILTER_DATE_LABEL,
  DEFAULT_FILTER_VISUAL_DATA_TYPE,
  DEFAULT_START_DATE,
} from 'common/constants';

export const REPORT_CREATE_CONFIRM_ID = 'CREATE_NEW_REPORT';

const HIDE_BUTTON_PAGES: string[] = [
  USER_ROUTES.root,
  REPORTS_ROUTES.root,
  ANALYZE_ROUTES.elsieai,
  ANALYZE_ROUTES.elsieaiV2,
  CAPTURE_ROUTES.root,
  PROFILE_CAPTURE_ROUTES.root,
  PLANNING_ROUTES.root,
  ALIGNMENT_ROUTES.compare,
  INSIGHTS_ROUTES.compare,
  COACH_ROUTES.summary,
];

interface CreateDashboardButtonProps {
  datasetId: string;
}

function CreateDashboardButton({ datasetId }: Readonly<CreateDashboardButtonProps>) {
  const [reportName, setReportName] = useState('');
  const [saveDashboard] = useCreateReportMutation();
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const { compareUrl } = useCompareUrlWithProjectId();
  const { hasFeature } = useFeatureFlags();
  const { isMobile } = useResize();
  const { chartApiLoadingState } = useApiLoadingState();
  const navigate = useNavigateWithProjectId();
  const page = useSelector(selectPage);
  const filters = useSelector((state: RootState) => selectCommonFilters(state, datasetId));
  const startDate = useSelector((state: RootState) => selectStartDate(state, datasetId));
  const endDate = useSelector((state: RootState) => selectEndDate(state, datasetId));
  const filterParent = useSelector(selectFilterParent);
  const displayDateType = useSelector((root: RootState) => selectVisualType(root, datasetId));
  const displayDateVisualData = useSelector((root: RootState) => selectVisualData(root, datasetId));
  const datasetQuestion = useSelector((state: RootState) =>
    selectDatasetQuestion(state, datasetId, projectId ?? '')
  );
  const widgetsAreLoading = chartApiLoadingState.isLoading || chartApiLoadingState.isFailed;
  const allWidgetOptions = useSelector((state: RootState) =>
    selectWidgetOptions(state, projectId ?? '')
  );
  const { saveMultipleOptions } = usePersistWidgetOptions({ id: '' });

  const hideButton =
    !page ||
    isMobile ||
    !!HIDE_BUTTON_PAGES.some((page) => compareUrl(page, { includes: true })) ||
    !hasFeature(Feature.SavePageAsCustomDashboard);

  const { data } = useDatasetQuestions({ skip: hideButton, projectId: projectId ?? '' });

  const currentCategory = datasetQuestion
    ? data?.find((category) =>
        category.questions.find((question) => question.id === datasetQuestion.id)
      )
    : undefined;

  const pageQuestionFilter: CommonFilterObject | undefined = datasetQuestion
    ? {
        label: CommonFilterList.Question,
        type: 1,
        value: [
          {
            name: datasetQuestion.name,
            value: datasetQuestion.id.toString(),
          },
        ],
      }
    : undefined;

  const pageCategoryFilter: CommonFilterObject | undefined = currentCategory
    ? {
        label: CommonFilterList.Survey,
        type: 1,
        value: [
          {
            name: currentCategory.name,
            value: currentCategory.id.toString(),
          },
        ],
      }
    : undefined;

  useEffect(() => {
    setReportName(page ? `${page.pageTitle} Report` : '');

    return () => {
      setReportName('');
    };
  }, [page]);

  const addToCustomReport = () => {
    dispatch(
      showConfirm({
        id: REPORT_CREATE_CONFIRM_ID,
        param: {},
      })
    );
  };

  const cloneReport = async (result: boolean) => {
    if (result) {
      const newWidgets = (page as PageComposerModel).widgets
        .filter((w) => !w.id?.includes('ai-chat') && !w.id?.includes('elsie'))
        .map((widget: WidgetModel) => {
          const nextWidget = {
            ...widget,
            id: uniqueString(),
            originalWidgetId: widget.id,
            defaultFilters: {
              filters: pageQuestionFilter
                ? {
                    ...filters,
                    [CommonFilterList.Survey]: pageCategoryFilter,
                    [CommonFilterList.Question]: pageQuestionFilter,
                  }
                : filters,
              startDate,
              endDate,
              filterParent,
              visualData: displayDateVisualData,
              visualDataType: displayDateType,
            },
          };

          return nextWidget;
        });

      const widgetOptionsArray: Record<string, WidgetOptions> = {};

      newWidgets.forEach((widget) => {
        const widgetOptions = allWidgetOptions.find(
          (option) => option.value.id === `${page?.pageName}-${widget.originalWidgetId}`
        );
        if (widgetOptions) {
          widgetOptionsArray[`-${widget.id}`] = { ...widgetOptions.value };
        }
      });

      await saveMultipleOptions(widgetOptionsArray);

      const report: ReportModel = {
        id: null,
        name: reportName,
        description: reportName,
        composition: {
          ...(page as PageComposerModel),
          pageName: '',
          widgets: newWidgets,
          waitForDatasetQuestion: false,
        },
        activeWidget: null,
        created_at: new Date().toJSON(),
        updated_at: new Date().toJSON(),
        isSubscribeAvailable: false,
        isSubscribed: false,
        overrideDateFilter: false,
        universalEndDate: DEFAULT_END_DATE,
        universalFilters: {},
        universalStartDate: DEFAULT_START_DATE,
        universalVisualData: DEFAULT_FILTER_DATE_LABEL,
        universalVisualDataType: DEFAULT_FILTER_VISUAL_DATA_TYPE,
      };

      saveDashboard(report)
        .then((result: any) => {
          dispatch(hideConfirm(REPORT_CREATE_CONFIRM_ID));
          navigate(`${REPORTS_ROUTES.view}/${result.data.toString()}`);
        })
        .catch((e) => {
          console.error('Failed to clone the page into report reason:', e);
          dispatch(hideConfirm(REPORT_CREATE_CONFIRM_ID));
        });
    } else {
      dispatch(hideConfirm(REPORT_CREATE_CONFIRM_ID));
    }
  };

  if (hideButton) {
    return null;
  }

  return (
    <>
      <KwelloIconButton
        icon={KWELLO_ICON.REPORT_MENU_ICON}
        onClick={addToCustomReport}
        tooltip="Save Page as Custom Dashboard"
        tooltipPosition="bottom"
        disabled={widgetsAreLoading}
      />
      <AppConfirm
        id={REPORT_CREATE_CONFIRM_ID}
        dialogTitle={'Save as Custom Dashboard'}
        proceed={(result: boolean) => cloneReport(result)}
        yesLabel="Save & View in Dashboard Library"
        yesDisabled={reportName == ''}
        hideNoLabel={true}
      >
        <Typography.p themeColor={'dark'}>
          Save this page as a custom dashboard to retain all applied filters. <br />
          You can then further personalize the dashboard by <br />
          editing content, adding tiles, and making filter adjustments
          <br /> as needed.
        </Typography.p>
        <TextBox
          defaultValue={reportName}
          onChange={(e: TextBoxChangeEvent) => setReportName(e.target.value as string)}
          title="Dashboard Name"
        />
      </AppConfirm>
    </>
  );
}

export default CreateDashboardButton;
